import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  approvePurchasePlan,
  cancelPurchasePlan,
  getPurchasePlanGoods,
  submitPurchasePlan,
  unapprovePurchasePlan,
  type PurchasePlanGoodsRow,
  type PurchasePlanRecord,
} from '@/api/purchase';
import { getUser, isDivideAccount } from '@/utils/auth';
import {
  clearDynamicBreadcrumbTitle,
  setDynamicBreadcrumbTitle,
} from '@/hooks/useDynamicBreadcrumb';
import type { ApprovalStep } from '@/types/order';
import MonthPicker from '@/components/base/MonthPicker';
import ApproveModal from './components/ApproveModal';
import CancelModal from './components/CancelModal';
import ConfirmModal from './components/ConfirmModal';
import UsageRatioBar from '@/components/base/UsageRatioBar';

type DotTone = 'done' | 'rejected' | 'current' | 'pending';

interface ApprovalNode {
  id: string;
  title: string;
  subtitle?: string;
  statusText?: string;
  statusTone?: DotTone;
  time?: string;
  comment?: string;
  dot: DotTone;
}

function mapApproveState(state: number | string | undefined): ApprovalStep['status'] {
  const n = Number(state);
  if (n === 1) return 'approved';
  if (n === 2) return 'rejected';
  return 'pending';
}

function mapInstanToSteps(instan: PurchasePlanRecord['instan']): ApprovalStep[] {
  if (!Array.isArray(instan) || instan.length === 0) return [];
  return instan
    .filter((flow) => {
      const sort = Number(flow.sort ?? 0);
      const nowSort = Number(flow.now_step_sort ?? 0);
      const isOver = Number(flow.is_over ?? 0);
      if (!flow.now_step_sort && flow.now_step_sort !== 0) return true;
      return sort <= nowSort || isOver !== 1;
    })
    .map((flow, index) => {
      const status = mapApproveState(flow.approve_state);
      const stepName = String(flow.step_name || flow.step || `第${index + 1}级审批`);
      const approverName =
        status === 'pending'
          ? String(flow.account_names || flow.account_name || '待定')
          : String(flow.account_name || flow.account_names || '');
      return {
        id: String(flow.id ?? `flow-${index}`),
        level: Number(flow.sort ?? index + 1) || index + 1,
        approverName,
        role: stepName,
        status,
        approvedAt: flow.approve_time ? String(flow.approve_time) : undefined,
        comment: flow.approve ? String(flow.approve) : undefined,
      };
    });
}

function getPurchaseStatusTag(status: number | string | undefined, statusText?: string) {
  const n = Number(status);
  const label = statusText || '-';
  if (n === 10) return { color: '#19be6b', label, icon: 'ri-checkbox-circle-line' };
  if (n === 1) return { color: '#2d8cf0', label, icon: 'ri-loader-4-line' };
  if (n === 2) return { color: '#ed4014', label, icon: 'ri-close-circle-line' };
  if (n === 99) return { color: '#c5c8ce', label, icon: 'ri-close-line' };
  return { color: '#33bcb7', label, icon: 'ri-time-line' };
}

function buildPurchaseApprovalNodes(row: PurchasePlanRecord): ApprovalNode[] {
  const status = Number(row.status);
  const inApproval = status === 0 || status === 1;
  const isRejected = status === 2;
  const isCancelled = status === 99;
  const steps = mapInstanToSteps(row.instan);
  const submitter = row.member?.nickname || row.member?.account || undefined;

  const nodes: ApprovalNode[] = [
    {
      id: 'submit',
      title: '提交采购计划',
      subtitle: submitter,
      time: row.created_at,
      dot: 'done',
    },
  ];

  if (isCancelled) {
    nodes.push({
      id: 'cancelled',
      title: '计划已取消',
      subtitle: submitter,
      statusText: '已取消',
      statusTone: 'rejected',
      dot: 'rejected',
    });
    return nodes;
  }

  if (steps.length > 0) {
    let currentMarked = false;
    steps.forEach((step) => {
      let dot: DotTone;
      let statusText: string;
      let subtitle: string | undefined;

      if (step.status === 'approved') {
        dot = 'done';
        statusText = '已审批';
        subtitle = step.approverName ? `${step.approverName}（同意）` : undefined;
      } else if (step.status === 'rejected') {
        dot = 'rejected';
        statusText = '已拒绝';
        subtitle = step.approverName ? `${step.approverName}（拒绝）` : undefined;
      } else if (!currentMarked && inApproval) {
        dot = 'current';
        statusText = '待审批';
        subtitle = step.approverName ? `等待 ${step.approverName} 审批` : undefined;
        currentMarked = true;
      } else {
        dot = 'pending';
        statusText = '待审批';
        subtitle = step.approverName ? `等待 ${step.approverName} 审批` : undefined;
      }

      nodes.push({
        id: step.id || `appr-${step.level}`,
        title: step.role,
        subtitle,
        statusText,
        statusTone: dot,
        time: step.approvedAt,
        comment: step.comment,
        dot,
      });
    });
  } else if (!isRejected) {
    nodes.push({
      id: 'approved',
      title: status === 10 ? '审批通过' : '待审批',
      statusText: status === 10 ? '已通过' : '待审批',
      statusTone: status === 10 ? 'done' : inApproval ? 'current' : 'pending',
      dot: status === 10 ? 'done' : inApproval ? 'current' : 'pending',
    });
  }

  return nodes;
}

function accountIdsInclude(
  accountIds: string | null | undefined,
  userId: string | number | null | undefined,
) {
  if (userId == null || userId === '') return false;
  const uid = String(userId).trim();
  if (!uid) return false;
  return String(accountIds || '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)
    .some((id) => id === uid);
}

function getCurrentApproveStep(row: PurchasePlanRecord) {
  const instan = row.instan;
  if (!Array.isArray(instan) || instan.length === 0) return null;
  const bySort = instan.find((f) => Number(f.sort) === Number(f.now_step_sort));
  if (bySort) return bySort;
  return instan.find((f) => Number(f.approve_state) === 0) || null;
}

function resolveCanApprove(row: PurchasePlanRecord) {
  const status = Number(row.status);
  if (status !== 0 && status !== 1) return false;
  const current = getCurrentApproveStep(row);
  if (!current || Number(current.approve_state) !== 0) return false;
  return accountIdsInclude(current.account_ids, getUser()?.id);
}

function resolveCanCancel(row: PurchasePlanRecord) {
  const status = Number(row.status);
  return status === 0 || status === 1;
}

/**
 * 反审权限：须已通过，且当前账号属于最近一次已通过步骤的审批人
 */
function resolveCanReverse(row: PurchasePlanRecord) {
  if (Number(row.status) !== 10) return false;
  const instan = Array.isArray(row.instan) ? row.instan : [];
  const lastPassed = [...instan]
    .filter((f) => Number(f.approve_state) === 1)
    .sort((a, b) => Number(b.sort ?? 0) - Number(a.sort ?? 0))[0];
  if (!lastPassed) return false;
  return accountIdsInclude(lastPassed.account_ids, getUser()?.id);
}

/** 审批中：本科室提交人或任一审批人可覆盖上传清单 */
function resolveCanEdit(row: PurchasePlanRecord) {
  const status = Number(row.status);
  if (status !== 0 && status !== 1) return false;
  const user = getUser();
  const uid = user?.id;
  if (uid == null || uid === '') return false;
  if (!isDivideAccount()) {
    const userDept = user?.department_id ?? user?.department?.id;
    const rowDept = row.office?.id;
    if (userDept != null && rowDept != null && String(userDept) !== String(rowDept)) {
      return false;
    }
  }
  if (row.account_id != null && String(row.account_id) === String(uid)) return true;
  return (row.instan || []).some((step) => accountIdsInclude(step.account_ids, uid));
}

function applyMonthToValue(month?: string | number) {
  const s = String(month || '').trim();
  if (/^\d{6}$/.test(s)) return `${s.slice(0, 4)}-${s.slice(4, 6)}`;
  if (/^\d{4}-\d{2}$/.test(s)) return s;
  return '';
}

function formatTime(value?: string) {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export default function PurchasePlanDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const flowId = String(id || '');

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [flow, setFlow] = useState<PurchasePlanRecord | null>(null);
  const [goods, setGoods] = useState<PurchasePlanGoodsRow[]>([]);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [modal, setModal] = useState<'approve' | 'cancel' | 'reverse' | null>(null);
  const [actionSubmitting, setActionSubmitting] = useState(false);
  const [editUploadOpen, setEditUploadOpen] = useState(false);
  const [editUploadFile, setEditUploadFile] = useState<File | null>(null);
  const [editUploadError, setEditUploadError] = useState('');
  const [editSubmitting, setEditSubmitting] = useState(false);
  const editFileInputRef = useRef<HTMLInputElement>(null);

  const showToast = useCallback((message: string, type: 'success' | 'error' = 'success') => {
    setToast({ message, type });
    window.setTimeout(() => setToast(null), 3000);
  }, []);

  const fetchDetail = useCallback(async () => {
    if (!flowId) {
      setLoadError('采购计划 ID 无效');
      setLoading(false);
      return;
    }
    setLoading(true);
    setLoadError('');
    try {
      const res = await getPurchasePlanGoods(flowId);
      const ok = res.error === false || Number(res.error) === 0;
      if (!ok || !res.data?.flow) {
        setFlow(null);
        setGoods([]);
        setLoadError(res.message || res.msg || '采购计划加载失败');
        return;
      }
      setFlow(res.data.flow);
      setGoods(Array.isArray(res.data.goods) ? res.data.goods : []);
    } catch {
      setFlow(null);
      setGoods([]);
      setLoadError('采购计划加载失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  }, [flowId]);

  useEffect(() => {
    void fetchDetail();
  }, [fetchDetail]);

  useEffect(() => {
    if (!flow) {
      clearDynamicBreadcrumbTitle();
      return;
    }
    const title =
      flow.apply_month_text && flow.office?.title
        ? `${flow.apply_month_text} [${flow.office.title}] 采购计划表`
        : flow.apply_month_text ||
          (flow.apply_month ? String(flow.apply_month) : '') ||
          '计划详情';
    setDynamicBreadcrumbTitle(title);
    return () => clearDynamicBreadcrumbTitle();
  }, [flow]);

  const approvalNodes = useMemo(
    () => (flow ? buildPurchaseApprovalNodes(flow) : []),
    [flow],
  );

  const categorySummary = Array.isArray(flow?.category_summary) ? flow!.category_summary! : [];
  const statusTag = flow
    ? getPurchaseStatusTag(flow.status, flow.status_text)
    : null;
  const canApprove = flow ? resolveCanApprove(flow) : false;
  const canCancel = flow ? resolveCanCancel(flow) : false;
  const canReverse = flow ? resolveCanReverse(flow) : false;
  const canEdit = flow ? resolveCanEdit(flow) : false;
  const editMonth = flow ? applyMonthToValue(flow.apply_month) : '';

  const openEditUpload = () => {
    setEditUploadFile(null);
    setEditUploadError('');
    if (editFileInputRef.current) editFileInputRef.current.value = '';
    setEditUploadOpen(true);
  };

  const handleEditUploadSubmit = async () => {
    if (!flow || editSubmitting) return;
    if (!editUploadFile) {
      setEditUploadError('请上传采购清单模板');
      return;
    }
    const name = editUploadFile.name.toLowerCase();
    if (!/\.(xlsx|xls|csv)$/.test(name)) {
      setEditUploadError('仅支持 xlsx / xls / csv 文件');
      return;
    }
    setEditSubmitting(true);
    try {
      const res = await submitPurchasePlan({
        month: editMonth,
        file: editUploadFile,
        flow_id: flow.id,
        mode: 'overwrite',
      });
      const ok = res.error === false || Number(res.error) === 0;
      if (!ok) {
        showToast(res.message || res.msg || '采购计划修改失败', 'error');
        return;
      }
      showToast(res.message || '采购计划清单已覆盖更新');
      setEditUploadOpen(false);
      void fetchDetail();
    } catch {
      showToast('网络异常，提交失败', 'error');
    } finally {
      setEditSubmitting(false);
    }
  };

  const totals = useMemo(() => {
    return goods.reduce(
      (acc, row) => {
        acc.planQty += Number(row.plan_quantity ?? 0) || 0;
        acc.usedQty += Number(row.used_quantity ?? row.sale_quantity ?? 0) || 0;
        acc.returnQty += Number(row.return_quantity ?? 0) || 0;
        acc.remainQty += Number(row.remain_quantity ?? row.limit_quantity ?? 0) || 0;
        acc.planAmount += Number(row.plan_amount ?? 0) || 0;
        acc.usedAmount += Number(row.used_amount ?? 0) || 0;
        acc.returnAmount += Number(row.return_amount ?? 0) || 0;
        acc.remainAmount += Number(row.remain_amount ?? 0) || 0;
        return acc;
      },
      {
        planQty: 0,
        usedQty: 0,
        returnQty: 0,
        remainQty: 0,
        planAmount: 0,
        usedAmount: 0,
        returnAmount: 0,
        remainAmount: 0,
      },
    );
  }, [goods]);

  const categoryTotals = useMemo(() => {
    return categorySummary.reduce(
      (acc, item) => {
        acc.qty += Number(item.plan_quantity ?? 0) || 0;
        acc.amount += Number(item.plan_amount ?? 0) || 0;
        return acc;
      },
      { qty: 0, amount: 0 },
    );
  }, [categorySummary]);

  const usageRatioTotal =
    totals.planAmount > 0
      ? ((totals.usedAmount - totals.returnAmount) / totals.planAmount) * 100
      : 0;

  const handleApproveSubmit = async (isAgree: boolean, remark: string) => {
    if (!flow) return;
    setActionSubmitting(true);
    try {
      const res = await approvePurchasePlan({
        flow_id: flow.id,
        is_agree: isAgree,
        remark,
      });
      const ok = res.error === false || Number(res.error) === 0;
      if (!ok) {
        showToast(res.message || res.msg || '审批失败', 'error');
        return;
      }
      showToast(isAgree ? '已同意审批' : '已拒绝审批');
      setModal(null);
      void fetchDetail();
    } catch {
      showToast('审批失败，请稍后重试', 'error');
    } finally {
      setActionSubmitting(false);
    }
  };

  const handleCancelSubmit = async (remark: string) => {
    if (!flow) return;
    setActionSubmitting(true);
    try {
      const res = await cancelPurchasePlan({
        flow_id: flow.id,
        remark,
      });
      const ok = res.error === false || Number(res.error) === 0;
      if (!ok) {
        showToast(res.message || res.msg || '取消失败', 'error');
        return;
      }
      showToast(res.message || '采购计划已取消');
      setModal(null);
      void fetchDetail();
    } catch {
      showToast('取消失败，请稍后重试', 'error');
    } finally {
      setActionSubmitting(false);
    }
  };

  const handleReverseConfirm = async () => {
    if (!flow) return;
    setActionSubmitting(true);
    try {
      const res = await unapprovePurchasePlan({
        flow_id: flow.id,
        remark: '反审退回',
      });
      const ok = res.error === false || Number(res.error) === 0;
      if (!ok) {
        showToast(res.message || res.msg || '反审失败', 'error');
        return;
      }
      showToast(res.message || '采购计划反审成功');
      setModal(null);
      void fetchDetail();
    } catch {
      showToast('反审失败，请稍后重试', 'error');
    } finally {
      setActionSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex-1 bg-background-100 flex items-center justify-center py-24">
        <div className="text-center">
          <i className="ri-loader-4-line animate-spin text-2xl text-foreground-400" />
          <p className="text-sm text-foreground-500 mt-3">正在加载计划详情...</p>
        </div>
      </div>
    );
  }

  if (loadError || !flow) {
    return (
      <div className="flex-1 bg-background-100">
        <div className="px-4 md:px-6 pt-0 pb-6 flex flex-col items-center justify-center py-24">
          <i className="ri-error-warning-line text-3xl text-primary-500 mb-3" />
          <p className="text-sm text-foreground-600 mb-4">{loadError || '采购计划不存在'}</p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => void fetchDetail()}
              className="px-4 py-2 rounded-lg border border-background-300 text-sm text-foreground-700 hover:bg-background-50"
            >
              重新加载
            </button>
            <button
              type="button"
              onClick={() => navigate('/purchase-plan')}
              className="px-4 py-2 rounded-lg bg-primary-500 text-background-50 text-sm hover:bg-primary-600"
            >
              返回列表
            </button>
          </div>
        </div>
      </div>
    );
  }

  const monthLabel =
    flow.apply_month_text || (flow.apply_month ? String(flow.apply_month) : '-');
  const departmentTitle = flow.office?.title || '-';
  const planTitle = `${monthLabel} [${departmentTitle}] 采购计划表`;

  return (
    <div className="flex-1 bg-background-100">
      {toast && (
        <div
          className={`fixed top-4 left-1/2 -translate-x-1/2 z-[130] px-4 py-2.5 rounded-lg text-sm font-medium shadow-lg flex items-center gap-2 ${
            toast.type === 'success'
              ? 'bg-accent-500 text-background-50'
              : 'bg-red-500 text-white'
          }`}
        >
          <i
            className={
              toast.type === 'success' ? 'ri-checkbox-circle-line' : 'ri-error-warning-line'
            }
          />
          {toast.message}
        </div>
      )}

      <div className="px-4 md:px-6 pt-0 pb-4 md:pb-6 space-y-4">
        <div className="bg-white rounded-lg border border-background-200 overflow-hidden">
          <div className="px-4 md:px-5 py-3 border-b border-background-100 flex items-center justify-between gap-3 flex-wrap">
            <div className="min-w-0 flex items-center gap-2 flex-wrap">
              <h1 className="text-base font-semibold text-foreground-900 truncate">
                {planTitle}
              </h1>
              {statusTag && (
                <span
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium text-white shrink-0"
                  style={{ backgroundColor: statusTag.color }}
                >
                  <i className={`${statusTag.icon} text-xs`} />
                  {statusTag.label}
                </span>
              )}
            </div>

            <div className="inline-flex items-center justify-end gap-1.5 flex-wrap shrink-0">
              {canApprove && (
                <button
                  type="button"
                  onClick={() => setModal('approve')}
                  className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs border border-primary-500 bg-primary-500 text-background-50 hover:bg-primary-600 hover:border-primary-600 transition-colors whitespace-nowrap"
                >
                  <i className="ri-shield-check-line text-xs" />
                  审批
                </button>
              )}
              {canReverse && (
                <button
                  type="button"
                  onClick={() => setModal('reverse')}
                  className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs border border-amber-300 text-amber-700 hover:bg-amber-50 hover:border-amber-400 transition-colors whitespace-nowrap"
                >
                  <i className="ri-arrow-go-back-line text-xs" />
                  反审
                </button>
              )}
              {canCancel && (
                <button
                  type="button"
                  onClick={() => setModal('cancel')}
                  className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs border border-primary-200 text-primary-600 hover:bg-primary-50 hover:border-primary-300 transition-colors whitespace-nowrap"
                >
                  <i className="ri-close-circle-line text-xs" />
                  取消
                </button>
              )}
              {canEdit && (
                <button
                  type="button"
                  onClick={openEditUpload}
                  className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs border border-background-200 text-foreground-600 hover:bg-background-50 hover:border-primary-300 hover:text-primary-600 transition-colors whitespace-nowrap"
                >
                  <i className="ri-upload-2-line text-xs" />
                  修改
                </button>
              )}
              <button
                type="button"
                onClick={() => navigate('/purchase-plan')}
                className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs border border-background-200 text-foreground-600 hover:bg-background-50 hover:border-primary-300 hover:text-primary-600 transition-colors whitespace-nowrap"
              >
                <i className="ri-arrow-left-line text-xs" />
                返回列表
              </button>
            </div>
          </div>

          <div className="p-4 md:p-5">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="rounded-lg bg-background-50 border border-background-100 px-3 py-2.5">
                <p className="text-[11px] text-foreground-400">计划数量合计</p>
                <p className="text-sm font-semibold text-foreground-900 mt-0.5">
                  {Number(flow.plan_quantity ?? totals.planQty)}
                </p>
              </div>
              <div className="rounded-lg bg-background-50 border border-background-100 px-3 py-2.5">
                <p className="text-[11px] text-foreground-400">计划金额合计</p>
                <p className="text-sm font-semibold text-primary-600 mt-0.5">
                  ¥{Number(flow.plan_amount ?? totals.planAmount).toFixed(2)}
                </p>
              </div>
              <div className="rounded-lg bg-background-50 border border-background-100 px-3 py-2.5">
                <p className="text-[11px] text-foreground-400">已使用 / 剩余</p>
                <p className="text-sm font-semibold text-foreground-900 mt-0.5">
                  {totals.usedQty} / {totals.remainQty}
                </p>
              </div>
              <div className="rounded-lg bg-background-50 border border-background-100 px-3 py-2.5">
                <p className="text-[11px] text-foreground-400">剩余金额</p>
                <p className="text-sm font-semibold text-foreground-900 mt-0.5">
                  ¥{totals.remainAmount.toFixed(2)}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="bg-white rounded-lg border border-background-200 p-4 lg:col-span-1">
            <h2 className="text-sm font-semibold text-foreground-900 mb-3">审批流程</h2>
            <div className="relative">
              <div className="absolute left-[7px] top-1.5 bottom-2 w-px bg-background-200" />
              {approvalNodes.map((node, nidx) => {
                const isLast = nidx === approvalNodes.length - 1;
                return (
                  <div key={node.id} className={`relative flex gap-2.5 ${isLast ? '' : 'pb-3'}`}>
                    <div className="relative z-10 shrink-0">
                      <div
                        className={`w-3.5 h-3.5 rounded-full flex items-center justify-center ${
                          node.dot === 'done'
                            ? 'bg-accent-500'
                            : node.dot === 'rejected'
                              ? 'bg-primary-500'
                              : node.dot === 'current'
                                ? 'bg-primary-500 ring-2 ring-primary-100'
                                : 'bg-background-100 border border-background-300'
                        }`}
                      >
                        {node.dot === 'done' && (
                          <i className="ri-check-line text-[9px] text-background-50" />
                        )}
                        {node.dot === 'rejected' && (
                          <i className="ri-close-line text-[9px] text-background-50" />
                        )}
                        {node.dot === 'pending' && (
                          <div className="w-1 h-1 rounded-full bg-background-400" />
                        )}
                      </div>
                    </div>
                    <div className="flex-1 min-w-0 -mt-px">
                      <div className="flex items-center flex-wrap gap-x-1.5 gap-y-0.5">
                        <span
                          className={`text-[11px] leading-relaxed ${
                            node.dot === 'pending'
                              ? 'text-foreground-400'
                              : node.dot === 'rejected'
                                ? 'text-primary-700 font-medium'
                                : 'text-foreground-800 font-medium'
                          }`}
                        >
                          {node.title}
                        </span>
                        {node.subtitle && (
                          <span className="text-[11px] text-foreground-400">{node.subtitle}</span>
                        )}
                        {node.statusText && (
                          <span
                            className={`inline-flex items-center gap-1 text-[11px] font-medium ${
                              node.statusTone === 'done'
                                ? 'text-accent-600'
                                : node.statusTone === 'rejected' || node.statusTone === 'current'
                                  ? 'text-primary-600'
                                  : 'text-foreground-400'
                            }`}
                          >
                            {node.statusTone === 'current' && (
                              <i className="ri-loader-4-line animate-spin text-[10px]" />
                            )}
                            {node.statusText}
                          </span>
                        )}
                      </div>
                      {node.time && (
                        <span className="text-[11px] text-foreground-400 block mt-0.5">
                          {formatTime(node.time)}
                        </span>
                      )}
                      {node.comment && (
                        <div className="mt-1.5 px-2 py-1 rounded bg-background-100 text-[11px] text-foreground-600">
                          <i className="ri-chat-quote-line text-foreground-400 mr-1" />
                          {node.comment}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="bg-white rounded-lg border border-background-200 p-4 lg:col-span-2">
              <h2 className="text-sm font-semibold text-foreground-900 mb-3">分类合计</h2>
              {categorySummary.length === 0 ? (
                <p className="py-8 text-center text-sm text-foreground-400">暂无分类汇总</p>
              ) : (
                <div className="overflow-x-auto rounded-lg border border-background-200">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-background-50 border-b border-background-200">
                        <th className="py-2 px-3 text-left font-semibold text-foreground-600">分类</th>
                        <th className="py-2 px-3 text-center font-semibold text-foreground-600">
                          计划数量
                        </th>
                        <th className="py-2 px-3 text-right font-semibold text-foreground-600">
                          计划金额
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {categorySummary.map((item, idx) => (
                        <tr
                          key={`${item.category_id ?? 'cat'}-${idx}`}
                          className="border-b border-background-100"
                        >
                          <td className="py-2 px-3 text-foreground-800">
                            {item.category_name || '未分类'}
                          </td>
                          <td className="py-2 px-3 text-center text-foreground-700">
                            {item.plan_quantity ?? 0}
                          </td>
                          <td className="py-2 px-3 text-right font-medium text-foreground-800">
                            ¥{Number(item.plan_amount ?? 0).toFixed(2)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="bg-background-50 border-t border-background-200">
                        <td className="py-2 px-3 text-xs font-semibold text-foreground-700">合计</td>
                        <td className="py-2 px-3 text-center text-xs font-bold text-foreground-900">
                          {categoryTotals.qty}
                        </td>
                        <td className="py-2 px-3 text-right text-xs font-bold text-primary-600">
                          ¥{categoryTotals.amount.toFixed(2)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>
        </div>

        <div className="bg-white rounded-lg border border-background-200 overflow-hidden">
          <div className="px-4 py-3 border-b border-background-100 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-foreground-900">计划商品明细</h2>
            <span className="text-xs text-foreground-500">共 {goods.length} 项</span>
          </div>
          {goods.length === 0 ? (
            <p className="py-12 text-center text-sm text-foreground-400">暂无商品明细</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse [&_th]:border [&_th]:border-background-200 [&_td]:border [&_td]:border-background-100">
                <thead>
                  <tr className="bg-background-50">
                    <th className="py-2.5 px-2 text-center text-xs font-semibold text-foreground-600 w-10 whitespace-nowrap">
                      序号
                    </th>
                    <th className="py-2.5 px-3 text-left text-xs font-semibold text-foreground-600 whitespace-nowrap">
                      商品名称
                    </th>
                    <th className="py-2.5 px-3 text-left text-xs font-semibold text-foreground-600 whitespace-nowrap">
                      规格名称
                    </th>
                    <th className="py-2.5 px-3 text-left text-xs font-semibold text-foreground-600 whitespace-nowrap">
                      编码
                    </th>
                    <th className="py-2.5 px-3 text-center text-xs font-semibold text-foreground-600 whitespace-nowrap">
                      单位
                    </th>
                    <th className="py-2.5 px-3 text-center text-xs font-semibold text-foreground-600 whitespace-nowrap">
                      计划数
                    </th>
                    <th className="py-2.5 px-3 text-center text-xs font-semibold text-foreground-600 whitespace-nowrap">
                      已使用
                    </th>
                    <th className="py-2.5 px-3 text-center text-xs font-semibold text-foreground-600 whitespace-nowrap">
                      已退货
                    </th>
                    <th className="py-2.5 px-3 text-center text-xs font-semibold text-foreground-600 whitespace-nowrap">
                      剩余可用
                    </th>
                    <th className="py-2.5 px-3 text-right text-xs font-semibold text-foreground-600 whitespace-nowrap">
                      单价
                    </th>
                    <th className="py-2.5 px-3 text-right text-xs font-semibold text-foreground-600 whitespace-nowrap">
                      采购总价
                    </th>
                    <th className="py-2.5 px-3 text-right text-xs font-semibold text-foreground-600 whitespace-nowrap">
                      已使用总价
                    </th>
                    <th className="py-2.5 px-3 text-right text-xs font-semibold text-foreground-600 whitespace-nowrap">
                      退货金额
                    </th>
                    <th className="py-2.5 px-3 text-right text-xs font-semibold text-foreground-600 whitespace-nowrap">
                      剩余金额
                    </th>
                    <th className="py-2.5 px-3 text-left text-xs font-semibold text-foreground-600 whitespace-nowrap">
                      已使用比例
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {goods.map((g, idx) => (
                    <tr
                      key={`${g.spec_id}-${idx}`}
                      className="hover:bg-background-50/60 transition-colors"
                    >
                      <td className="py-2.5 px-2 text-center text-foreground-500 text-xs">
                        {idx + 1}
                      </td>
                      <td className="py-2.5 px-3 text-foreground-800 text-xs max-w-[140px] truncate">
                        {g.goods_name || g.goods?.title || '-'}
                      </td>
                      <td className="py-2.5 px-3 text-foreground-600 text-xs max-w-[120px] truncate">
                        {g.spec_name || g.spec?.name || '-'}
                      </td>
                      <td className="py-2.5 px-3 text-foreground-500 text-xs whitespace-nowrap">
                        {g.code || g.spec?.sku || '-'}
                      </td>
                      <td className="py-2.5 px-3 text-center text-foreground-600 text-xs whitespace-nowrap">
                        {g.unit || g.spec?.unit || '-'}
                      </td>
                      <td className="py-2.5 px-3 text-center text-foreground-700 text-xs font-medium whitespace-nowrap">
                        {Number(g.plan_quantity ?? 0) || 0}
                      </td>
                      <td className="py-2.5 px-3 text-center text-foreground-700 text-xs whitespace-nowrap">
                        {Number(g.used_quantity ?? g.sale_quantity ?? 0) || 0}
                      </td>
                      <td className="py-2.5 px-3 text-center text-foreground-600 text-xs whitespace-nowrap">
                        {Number(g.return_quantity ?? 0) || 0}
                      </td>
                      <td className="py-2.5 px-3 text-center text-foreground-800 text-xs font-medium whitespace-nowrap">
                        {Number(g.remain_quantity ?? g.limit_quantity ?? 0) || 0}
                      </td>
                      <td className="py-2.5 px-3 text-right text-foreground-700 text-xs whitespace-nowrap">
                        ¥{Number(g.price ?? 0).toFixed(2)}
                      </td>
                      <td className="py-2.5 px-3 text-right text-foreground-700 text-xs whitespace-nowrap">
                        ¥{Number(g.plan_amount ?? 0).toFixed(2)}
                      </td>
                      <td className="py-2.5 px-3 text-right text-foreground-700 text-xs whitespace-nowrap">
                        ¥{Number(g.used_amount ?? 0).toFixed(2)}
                      </td>
                      <td className="py-2.5 px-3 text-right text-foreground-600 text-xs whitespace-nowrap">
                        ¥{Number(g.return_amount ?? 0).toFixed(2)}
                      </td>
                      <td className="py-2.5 px-3 text-right text-primary-600 text-xs font-semibold whitespace-nowrap">
                        ¥{Number(g.remain_amount ?? 0).toFixed(2)}
                      </td>
                      <td className="py-2.5 px-3 text-left">
                        <UsageRatioBar ratio={Number(g.usage_ratio ?? 0) || 0} />
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-background-50">
                    <td
                      colSpan={5}
                      className="py-2.5 px-3 text-right text-xs font-medium text-foreground-700"
                    >
                      合计
                    </td>
                    <td className="py-2.5 px-3 text-center text-xs font-bold text-foreground-900">
                      {totals.planQty}
                    </td>
                    <td className="py-2.5 px-3 text-center text-xs font-bold text-foreground-900">
                      {totals.usedQty}
                    </td>
                    <td className="py-2.5 px-3 text-center text-xs font-bold text-foreground-900">
                      {totals.returnQty}
                    </td>
                    <td className="py-2.5 px-3 text-center text-xs font-bold text-foreground-900">
                      {totals.remainQty}
                    </td>
                    <td />
                    <td className="py-2.5 px-3 text-right text-xs font-bold text-foreground-900">
                      ¥{totals.planAmount.toFixed(2)}
                    </td>
                    <td className="py-2.5 px-3 text-right text-xs font-bold text-foreground-900">
                      ¥{totals.usedAmount.toFixed(2)}
                    </td>
                    <td className="py-2.5 px-3 text-right text-xs font-bold text-foreground-900">
                      ¥{totals.returnAmount.toFixed(2)}
                    </td>
                    <td className="py-2.5 px-3 text-right text-xs font-bold text-primary-600">
                      ¥{totals.remainAmount.toFixed(2)}
                    </td>
                    <td className="py-2.5 px-3 text-left">
                      <UsageRatioBar ratio={usageRatioTotal} barClassName="w-28" />
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>
      </div>

      <ApproveModal
        isOpen={modal === 'approve'}
        monthLabel={monthLabel}
        department={flow.office?.title || ''}
        submitter={flow.member?.nickname || flow.member?.account || ''}
        createdAt={flow.created_at || ''}
        categories={categorySummary.map((item) => ({
          name: item.category_name || '未分类',
          planQuantity: Number(item.plan_quantity ?? 0) || 0,
          planAmount: Number(item.plan_amount ?? 0) || 0,
        }))}
        submitting={actionSubmitting}
        onCancel={() => {
          if (!actionSubmitting) setModal(null);
        }}
        onApprove={(remark) => void handleApproveSubmit(true, remark)}
        onReject={(remark) => void handleApproveSubmit(false, remark)}
      />

      <CancelModal
        isOpen={modal === 'cancel'}
        monthLabel={monthLabel}
        submitting={actionSubmitting}
        onCancel={() => {
          if (!actionSubmitting) setModal(null);
        }}
        onConfirm={handleCancelSubmit}
      />

      <ConfirmModal
        isOpen={modal === 'reverse'}
        title="反审确认"
        message={`确认对 ${monthLabel || '该'} 采购计划执行反审？反审后将回到审批中，可重新审批。`}
        confirmLabel="确认反审"
        danger
        loading={actionSubmitting}
        onCancel={() => {
          if (!actionSubmitting) setModal(null);
        }}
        onConfirm={() => void handleReverseConfirm()}
      />

      {editUploadOpen && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/40 p-4">
          <div className="bg-background-50 rounded-xl w-full max-w-lg shadow-2xl overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-background-200">
              <div>
                <h3 className="text-sm font-semibold text-foreground-900">修改计划表</h3>
                <p className="text-xs text-foreground-500 mt-0.5">
                  上传新清单将覆盖当前审批中的采购计划商品
                </p>
              </div>
              <button
                type="button"
                disabled={editSubmitting}
                onClick={() => setEditUploadOpen(false)}
                className="w-8 h-8 flex items-center justify-center rounded-lg text-foreground-400 hover:text-foreground-700 hover:bg-background-100 disabled:opacity-50"
              >
                <i className="ri-close-line text-lg" />
              </button>
            </div>
            <div className="px-5 py-4 space-y-4">
              <div>
                <label className="text-sm font-medium text-foreground-800 mb-1.5 block">
                  <span className="text-red-500 mr-0.5">*</span>
                  计划月份
                </label>
                <MonthPicker value={editMonth} onChange={() => undefined} disabled />
              </div>
              <div>
                <label
                  className={`text-sm font-medium mb-1.5 block ${
                    editUploadError ? 'text-red-600' : 'text-foreground-800'
                  }`}
                >
                  <span className="text-red-500 mr-0.5">*</span>
                  采购清单模板
                </label>
                <input
                  ref={editFileInputRef}
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  className="hidden"
                  onChange={(e) => {
                    setEditUploadFile(e.target.files?.[0] || null);
                    setEditUploadError('');
                  }}
                />
                <div
                  className={`rounded-lg border border-dashed px-4 py-4 ${
                    editUploadError
                      ? 'border-red-500 ring-1 ring-red-200 bg-red-50'
                      : 'border-background-300 bg-background-50'
                  }`}
                >
                  {editUploadFile ? (
                    <div className="flex items-center justify-between gap-3">
                      <p className="text-sm text-foreground-800 truncate">{editUploadFile.name}</p>
                      <button
                        type="button"
                        disabled={editSubmitting}
                        onClick={() => {
                          setEditUploadFile(null);
                          if (editFileInputRef.current) editFileInputRef.current.value = '';
                        }}
                        className="text-xs text-foreground-500 hover:text-red-600"
                      >
                        移除
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => editFileInputRef.current?.click()}
                      className="w-full text-center py-2"
                    >
                      <i
                        className={`ri-upload-cloud-2-line text-2xl ${
                          editUploadError ? 'text-red-400' : 'text-foreground-300'
                        }`}
                      />
                      <p
                        className={`text-sm mt-1 ${
                          editUploadError ? 'text-red-600' : 'text-foreground-600'
                        }`}
                      >
                        点击上传采购清单模板
                      </p>
                    </button>
                  )}
                </div>
                {editUploadError && (
                  <p className="mt-1.5 text-xs text-red-600 flex items-center gap-1 font-medium">
                    <i className="ri-error-warning-line" />
                    {editUploadError}
                  </p>
                )}
              </div>
            </div>
            <div className="px-5 py-4 border-t border-background-200 flex items-center justify-end gap-2">
              <button
                type="button"
                disabled={editSubmitting}
                onClick={() => setEditUploadOpen(false)}
                className="px-4 py-2 rounded-lg border border-background-300 text-sm text-foreground-600 hover:bg-background-100 disabled:opacity-50"
              >
                取消
              </button>
              <button
                type="button"
                disabled={editSubmitting}
                onClick={() => void handleEditUploadSubmit()}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium disabled:opacity-50"
              >
                {editSubmitting ? (
                  <i className="ri-loader-4-line animate-spin" />
                ) : (
                  <i className="ri-send-plane-line" />
                )}
                {editSubmitting ? '提交中...' : '确认覆盖'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
